// Limits the API enforces, restated for the sentences that need them.
//
// W39.72. Two rounds of "the same number, stated twice, one copy drifted": the
// upload cap (8 MB vs 50 MB, on one endpoint) and the restore windows (7 vs 30,
// in ten sentences). Both were the same defect and both were found by reading,
// so this file is the third of its kind — the numbers below are the remaining
// ones that appear on both sides, and the sweep that found them.
//
//   * the sweep is a grep, which can miss, so "no drift today" is not a
//     property worth relying on. These are pinned instead.
//
// Each constant names the backend symbol it mirrors. **That link is a comment,
// not a check** — the two languages cannot import each other and there is no
// generated artifact carrying service-level constants (`openapi.yaml` carries
// schema bounds; these are not schema bounds). So the pair of tests, this one
// and `backend/tests/limits/test_server_says_the_truth.py`, is what keeps the
// two sides honest: this one pins the client, that one pins the server, and a
// window moved on one side without the other turns up as a failure on whichever
// side a person is actually reading.
//
// What is deliberately *not* here: the per-row `days_left` a trash screen
// shows, which the server computes and the client renders. That one is already
// derived, which is the only reason it has never drifted.

/** `app.modules.calendar.models.MAX_RANGE_DAYS` */
export const CALENDAR_RANGE_DAYS = 366;

/** `app.modules.album.storage.MAX_FILES` (w3.md §5.2: "max 20 files") */
export const MAX_UPLOAD_FILES = 20;

/**
 * The one number a user is told about.
 *
 * W52.5 (2026-10-13). This file used to carry **two** upload sizes — a 50 MB
 * `MAX_UPLOAD_BYTES` mirroring `album.storage.MAX_BYTES`, and a 25 MB
 * `MAX_UPLOAD_REQUEST_BYTES` describing nginx's `client_max_body_size`. The
 * second existed for a real reason (see below) and it was still true when it
 * was written. It stopped being true because the 25 MB was never a product
 * decision: `deploy/nginx/heshe.cyberpduc.com.conf` justified it with
 * 「后端本身不限制体积」, and the backend has capped files at 50 MB since
 * W39.69. nginx has been raised to 52 MB (50 + multipart framing), so the
 * split is gone and the user-facing number is the backend's own again.
 *
 * What it cost, measured on production rather than guessed: picking three
 * photos uploaded one and refused two, and **neither refusal appears in any
 * log** — `client_max_body_size` was never involved, because the client
 * refused to call `fetch` at all. A file the app cannot compress (HEIC on a
 * browser that cannot decode it, video, a decode failure) therefore had **no
 * size at all** it could ever be uploaded at: compressing it is refused on
 * purpose, and every uncompressed size above 25 MB was rejected client-side.
 *
 * So the number is back to being one thing again, and `MAX_UPLOAD_REQUEST_BYTES`
 * is gone rather than re-pointed — a second constant for "the size nginx
 * happens to allow" is what made this drift invisible in the first place.
 *
 * nginx still sits above this (`52m`) on purpose: an exactly-50 MB file
 * wrapped in multipart boundaries is slightly over 50 MB, and pinning nginx
 * *at* the limit would reject a file the backend accepts. That margin is
 * framing, not product — it must never become a number in a sentence.
 */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
